// src/components/CompanionLevelUpManager.js
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  applyPendingUpgrade,
  COMPANION_UPGRADES,
  COMPANION_EXPERIENCES
} from '../services/companionService';

const CompanionLevelUpManager = ({ companion, rangerId, onComplete }) => {
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [upgradeData, setUpgradeData] = useState({});
  const [applying, setApplying] = useState(false);

  const pendingUpgrades = companion.pendingUpgrades || 0;
  const currentLevel = companion.level - pendingUpgrades + 1;

  // Reset state when upgrade is changed
  const handleSelectUpgrade = (upgrade) => {
    setSelectedUpgrade(upgrade);
    setUpgradeData({});
  };

  // Apply the upgrade
  const handleApplyUpgrade = async () => {
    if (!selectedUpgrade) {
      toast.error('Please select an upgrade');
      return;
    }

    // Validate upgrade-specific data
    if (selectedUpgrade.id === 'intelligent') {
      if (!upgradeData.experienceToImprove) {
        toast.error('Please choose an experience to improve');
        return;
      }
    }

    if (selectedUpgrade.id === 'vicious') {
      if (!upgradeData.upgradeType) {
        toast.error('Please choose to upgrade damage or range');
        return;
      }
    }

    setApplying(true);
    try {
      const remaining = await applyPendingUpgrade(
        rangerId,
        companion.id,
        selectedUpgrade,
        upgradeData
      );

      if (remaining === 0) {
        // All upgrades applied
        onComplete();
      } else {
        // More upgrades to apply - reset state
        setSelectedUpgrade(null);
        setUpgradeData({});
        // Reload companion data by calling onComplete (parent will reload)
        onComplete();
      }
    } catch (error) {
      console.error('Error applying upgrade:', error);
    } finally {
      setApplying(false);
    }
  };

  // Can the upgrade be applied based on current state?
  const canApplyUpgrade = () => {
    if (!selectedUpgrade) return false;

    switch (selectedUpgrade.id) {
      case 'intelligent':
        return !!upgradeData.experienceToImprove;
      case 'vicious':
        // Check if there's room to upgrade
        if (upgradeData.upgradeType === 'damage') {
          const dieProgression = ['d6', 'd8', 'd10', 'd12'];
          const currentIndex = dieProgression.indexOf(companion.damageDie);
          return currentIndex < dieProgression.length - 1;
        } else if (upgradeData.upgradeType === 'range') {
          const rangeProgression = ['Melee', 'Close', 'Far'];
          const currentIndex = rangeProgression.indexOf(companion.damageRange);
          return currentIndex < rangeProgression.length - 1;
        }
        return !!upgradeData.upgradeType;
      default:
        return true;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-amber-400 mb-2">
          Level Up {companion.name}!
        </h2>
        <p className="text-gray-300">
          Level {currentLevel} → {companion.level}
          {pendingUpgrades > 1 && (
            <span className="ml-2 text-sm text-amber-400 font-medium">
              ({pendingUpgrades} upgrades remaining)
            </span>
          )}
        </p>
      </div>

      {/* Upgrade Selection */}
      <div>
        <h3 className="text-xl font-bold text-amber-400 mb-4">Choose an Upgrade</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {COMPANION_UPGRADES.map((upgrade) => {
            const isSelected = selectedUpgrade?.id === upgrade.id;

            return (
              <div
                key={upgrade.id}
                onClick={() => handleSelectUpgrade(upgrade)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-amber-500 bg-amber-900/20 shadow-md shadow-amber-500/20'
                    : 'border-gray-700 bg-gray-800/50 hover:border-amber-400 hover:bg-gray-800'
                }`}
              >
                <h3 className="font-bold text-lg mb-2 text-gray-100">{upgrade.name}</h3>
                <p className="text-sm text-gray-300">
                  {upgrade.description}
                </p>
                {upgrade.affectsRanger && (
                  <p className="text-xs text-amber-400 mt-2 font-medium">
                    ⭐ Affects Ranger
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Upgrade-Specific Options */}
        {selectedUpgrade && (
          <div className="border-t border-gray-700 pt-6 mt-6">
            <h3 className="font-bold text-lg mb-4 text-amber-300">
              Configure: {selectedUpgrade.name}
            </h3>

            {/* Intelligent: Choose experience to improve */}
            {selectedUpgrade.id === 'intelligent' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-300 mb-3">
                  Choose an existing experience to improve by +1:
                </p>

                <div className="space-y-2">
                  {companion.experiences.map((exp) => (
                    <label
                      key={exp.id}
                      className="flex items-center gap-3 p-3 border border-gray-700 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800 hover:border-amber-400 transition-all"
                    >
                      <input
                        type="radio"
                        name="intelligent-choice"
                        checked={upgradeData.experienceToImprove === exp.id}
                        onChange={() =>
                          setUpgradeData({
                            experienceToImprove: exp.id
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-200">
                        {exp.name} (+{exp.bonus} → +{exp.bonus + 1})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Vicious: Choose damage or range */}
            {selectedUpgrade.id === 'vicious' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-300 mb-3">
                  Choose to upgrade damage die or range:
                </p>

                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-gray-700 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800 hover:border-amber-400 transition-all">
                    <input
                      type="radio"
                      name="vicious-choice"
                      checked={upgradeData.upgradeType === 'damage'}
                      onChange={() =>
                        setUpgradeData({ upgradeType: 'damage' })
                      }
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-100">Upgrade Damage Die</p>
                      <p className="text-sm text-gray-300">
                        Current: {companion.damageDie} →{' '}
                        {companion.damageDie === 'd6' && 'd8'}
                        {companion.damageDie === 'd8' && 'd10'}
                        {companion.damageDie === 'd10' && 'd12'}
                        {companion.damageDie === 'd12' && '(max)'}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-gray-700 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800 hover:border-amber-400 transition-all">
                    <input
                      type="radio"
                      name="vicious-choice"
                      checked={upgradeData.upgradeType === 'range'}
                      onChange={() => setUpgradeData({ upgradeType: 'range' })}
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-100">Upgrade Range</p>
                      <p className="text-sm text-gray-300">
                        Current: {companion.damageRange} →{' '}
                        {companion.damageRange === 'Melee' && 'Close'}
                        {companion.damageRange === 'Close' && 'Far'}
                        {companion.damageRange === 'Far' && '(max)'}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Other upgrades don't need additional input */}
            {selectedUpgrade.id !== 'intelligent' &&
              selectedUpgrade.id !== 'vicious' && (
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-700">
                  <p className="text-sm text-green-300 font-medium">
                    ✓ This upgrade is ready to apply!
                  </p>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={handleApplyUpgrade}
          disabled={!canApplyUpgrade() || applying}
          className="px-8 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg"
        >
          {applying
            ? 'Applying...'
            : pendingUpgrades > 1
            ? 'Apply & Continue'
            : 'Apply Upgrade'}
        </button>
      </div>
    </div>
  );
};

export default CompanionLevelUpManager;
